import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, UserPlus, Users, MessageCircle, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useClinic } from "@/store/ClinicContext";
import { PatientFields, EMPTY_PATIENT } from "@/components/pacientes/PatientFields";
import { PatientProfile } from "@/components/pacientes/PatientProfile";
import { isoToBr, waLink } from "@/components/pacientes/patientApi";

const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export const NewPatientDialog = ({ open, onOpenChange, onCreated }) => {
  const { addPatient, healthPlans } = useClinic();
  const [p, setP] = useState(EMPTY_PATIENT);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!p.name.trim()) return toast.error("Informe o nome do paciente.");
    setSaving(true);
    const created = await addPatient(p).finally(() => setSaving(false));
    if (!created) return toast.error("Não foi possível cadastrar o paciente.");
    toast.success("Paciente cadastrado!", { description: p.name });
    setP(EMPTY_PATIENT);
    onOpenChange(false);
    onCreated?.(created, p);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="med-patient-modal" className="max-w-2xl">
        <DialogHeader><DialogTitle>Cadastrar Novo Paciente</DialogTitle><DialogDescription>Dados pessoais, contato, endereço e informações médicas.</DialogDescription></DialogHeader>
        <div className="max-h-[65vh] overflow-y-auto py-2 pr-1"><PatientFields value={p} onChange={setP} plans={healthPlans} /></div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancelar</button>
          <button data-testid="med-patient-save-btn" onClick={save} disabled={saving} className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-60">{saving ? "Cadastrando…" : "Cadastrar"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ p, onOpen }) => (
  <div data-testid={`patient-row-${p.id}`} onClick={() => onOpen(p.id)} className="flex cursor-pointer flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3.5 transition-colors hover:bg-slate-50 sm:flex-nowrap sm:px-6">
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-50 text-sm font-bold text-blue-700">{(p.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}</div>
    <div className="min-w-0 flex-1 basis-40">
      <p className="truncate font-semibold text-slate-900">{p.name}</p>
      <p className="truncate text-xs text-slate-500">{[p.cpf, p.phone, p.convenio].filter(Boolean).join(" · ") || "Sem dados de contato"}</p>
    </div>
    <span className="hidden text-xs text-slate-500 md:block">{p.last_visit ? `Última: ${isoToBr(p.last_visit)}` : "Sem consultas"}</span>
    <span data-testid={`patient-status-${p.id}`} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${p.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{p.active ? "Ativo" : "Inativo"}</span>
    {waLink(p.phone, p.name) && (
      <a data-testid={`patient-wa-${p.id}`} href={waLink(p.phone, p.name)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title="Abrir no WhatsApp" className="rounded-lg p-1.5 text-emerald-600 hover:bg-emerald-50"><MessageCircle className="h-4 w-4" /></a>
    )}
    <ChevronRight className="hidden h-4 w-4 text-slate-300 sm:block" />
  </div>
);

export const PacientesModule = () => {
  const { patients, healthPlans } = useClinic();
  const [q, setQ] = useState("");
  const [conv, setConv] = useState("todos");
  const [status, setStatus] = useState("todos");
  const [newOpen, setNewOpen] = useState(false);
  const [openId, setOpenId] = useState(null);

  const list = useMemo(() => {
    const t = norm(q).trim();
    const td = q.replace(/\D/g, "");
    return (patients || []).filter((p) => {
      if (conv !== "todos" && (p.convenio || "Particular") !== conv) return false;
      if (status !== "todos" && (status === "ativo") !== !!p.active) return false;
      if (!t) return true;
      return norm(p.name).includes(t) || (td.length >= 3 && ((p.cpf || "").replace(/\D/g, "").includes(td) || (p.phone || "").replace(/\D/g, "").includes(td)));
    });
  }, [patients, q, conv, status]);

  if (openId) return <PatientProfile id={openId} onBack={() => setOpenId(null)} />;
  return (
    <div data-testid="pacientes-module" className="rounded-2xl border border-slate-200/70 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-4 sm:px-6">
        <div>
          <h3 className="font-heading flex items-center gap-2 text-lg font-bold text-slate-900"><Users className="h-5 w-5 text-blue-600" />Pacientes</h3>
          <p data-testid="patients-count" className="text-xs text-slate-500">{list.length} de {(patients || []).length} pacientes</p>
        </div>
        <button data-testid="new-patient-btn" onClick={() => setNewOpen(true)} className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-blue-700"><UserPlus className="h-4 w-4" />Cadastrar Novo Paciente</button>
      </div>
      <div className="flex flex-col gap-2 border-b border-slate-100 px-4 py-3 sm:flex-row sm:px-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input data-testid="patients-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome, CPF ou telefone" className="w-full rounded-xl border border-slate-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-blue-200" />
        </div>
        <div className="grid grid-cols-2 gap-2 sm:w-80">
          <Select value={conv} onValueChange={setConv}>
            <SelectTrigger data-testid="patients-convenio-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="todos">Todos convênios</SelectItem>{healthPlans.map((h) => <SelectItem key={h.id} value={h.name}>{h.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="patients-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="todos">Todos status</SelectItem><SelectItem value="ativo">Ativos</SelectItem><SelectItem value="inativo">Inativos</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {list.length === 0 ? <p data-testid="patients-empty" className="px-6 py-12 text-center text-sm text-slate-400">Nenhum paciente encontrado.</p> : list.map((p) => <Row key={p.id} p={p} onOpen={setOpenId} />)}
      </div>
      <p className="px-6 py-3 text-[11px] text-slate-400">Ativo: consulta futura ou nos últimos 12 meses.</p>
      <NewPatientDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(c) => c?.id && setOpenId(c.id)} />
    </div>
  );
};
